import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Vote, CheckCircle2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import "./HomePolls.css";

// Client fingerprint for dedup (stored in localStorage)
function getFingerprint() {
  let fp = localStorage.getItem("popu-fp");
  if (!fp) {
    fp = "fp-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
    localStorage.setItem("popu-fp", fp);
  }
  return fp;
}

function votedKey(slug) { return `popu-voted-${slug}`; }

export default function HomePolls() {
  const [polls, setPolls] = useState([]);

  useEffect(() => {
    api.get("/polls/public").then((r) => setPolls(r.data || [])).catch(() => setPolls([]));
  }, []);

  if (!polls.length) return null;

  return (
    <section className="polls-mod" data-testid="home-polls">
      <header className="polls-head">
        <Vote size={26} className="polls-icon" />
        <div>
          <h2 className="polls-title">TU OPINIÓN CUENTA</h2>
          <p className="polls-sub">Participá en las encuestas abiertas</p>
        </div>
      </header>
      <div className="polls-grid">
        {polls.map((p) => <PollCard key={p.id} poll={p} />)}
      </div>
    </section>
  );
}

export function PollCard({ poll }) {
  const [selected, setSelected] = useState(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (localStorage.getItem(votedKey(poll.slug))) setSubmitted(true);
  }, [poll.slug]);

  const toggle = (id) => {
    if (submitted) return;
    if (poll.allow_multiple) {
      const next = new Set(selected);
      if (next.has(id)) next.delete(id); else next.add(id);
      setSelected(next);
    } else {
      setSelected(new Set([id]));
    }
  };

  const submitVote = async (optionId) => {
    setBusy(true); setError("");
    try {
      await api.post(`/polls/by-slug/${poll.slug}/vote`, {
        option_ids: [optionId],
        fingerprint: getFingerprint(),
      });
      localStorage.setItem(votedKey(poll.slug), "1");
      setSubmitted(true);
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al votar";
      setError(msg);
      if (e.response?.status === 409) {
        localStorage.setItem(votedKey(poll.slug), "1");
        setSubmitted(true);
      }
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (selected.size === 0) { setError("Seleccioná una opción"); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/polls/by-slug/${poll.slug}/vote`, {
        option_ids: [...selected],
        fingerprint: getFingerprint(),
      });
      localStorage.setItem(votedKey(poll.slug), "1");
      setSubmitted(true);
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al votar";
      setError(msg);
      if (e.response?.status === 409) {
        localStorage.setItem(votedKey(poll.slug), "1");
        setSubmitted(true);
      }
    } finally { setBusy(false); }
  };

  const options = poll.options || [];
  const showInlineResults = !!poll.home_show_results;
  const isCards = poll.home_display === "cards";

  // CARDS layout (reference-style: big square photos, name, golden VOTAR)
  if (isCards) {
    return (
      <div className="poll-card poll-card-cards" data-testid={`poll-card-${poll.slug}`}>
        <div className="poll-body">
          <h3 className="poll-card-title">{poll.title}</h3>
          {poll.question && <p className="poll-card-question">{poll.question}</p>}
          {submitted ? (
            <div className="poll-thankyou">
              <CheckCircle2 size={26} className="text-green-500" />
              <p>¡Gracias por votar!</p>
              {showInlineResults && (
                <Link to={`/encuesta/${poll.slug}/resultados`} className="poll-see-results" data-testid={`poll-see-results-${poll.slug}`}>
                  Ver resultados <ExternalLink size={12} />
                </Link>
              )}
            </div>
          ) : (
            <>
              {error && <p className="poll-error">{error}</p>}
              <div className="poll-grid-cards">
                {options.map((o) => (
                  <div key={o.id} className="poll-grid-card" data-testid={`poll-grid-opt-${poll.slug}-${o.id}`}>
                    <button
                      type="button"
                      className="poll-grid-photo-btn"
                      onClick={() => setLightbox({ src: o.image, name: o.text })}
                      title="Ver imagen"
                      data-testid={`poll-photo-btn-${poll.slug}-${o.id}`}
                    >
                      {o.image ? (
                        <img src={o.image} alt={o.text} className="poll-grid-photo" />
                      ) : (
                        <div className="poll-grid-photo poll-grid-photo-empty" />
                      )}
                    </button>
                    <p className="poll-grid-name">{o.text}</p>
                    {showInlineResults && (o.pct != null) && (
                      <>
                        <p className="poll-grid-pct">{o.pct.toFixed(1)}%</p>
                        <div className="poll-grid-bar"><div className="poll-grid-bar-fill" style={{ width: `${o.pct}%` }} /></div>
                      </>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => submitVote(o.id)}
                      className="poll-btn-vote"
                      data-testid={`poll-vote-btn-${poll.slug}-${o.id}`}
                    >
                      VOTAR
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        {lightbox && (
          <div className="poll-lightbox" onClick={() => setLightbox(null)} data-testid="poll-lightbox">
            <button className="poll-lightbox-close" onClick={() => setLightbox(null)} aria-label="Cerrar">✕</button>
            <div className="poll-lightbox-inner" onClick={(e) => e.stopPropagation()}>
              {lightbox.src && <img src={lightbox.src} alt={lightbox.name} className="poll-lightbox-img" />}
              <p className="poll-lightbox-name">{lightbox.name}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // LIST layout (default)
  return (
    <div className="poll-card" data-testid={`poll-card-${poll.slug}`}>
      {poll.image && <img src={poll.image} alt={poll.title} className="poll-hero" />}
      <div className="poll-body">
        <h3 className="poll-card-title">{poll.title}</h3>
        {poll.question && <p className="poll-card-question">{poll.question}</p>}
        {submitted ? (
          <div className="poll-thankyou">
            <CheckCircle2 size={26} className="text-green-500" />
            <p>¡Gracias por votar!</p>
            {showInlineResults && (
              <Link to={`/encuesta/${poll.slug}/resultados`} className="poll-see-results" data-testid={`poll-see-results-${poll.slug}`}>
                Ver resultados <ExternalLink size={12} />
              </Link>
            )}
          </div>
        ) : (
          <>
            <ul className="poll-options">
              {options.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    className={`poll-option ${selected.has(o.id) ? "selected" : ""}`}
                    onClick={() => toggle(o.id)}
                    data-testid={`poll-opt-${poll.slug}-${o.id}`}
                  >
                    {o.image && (
                      <img
                        src={o.image}
                        alt={o.text}
                        className="poll-opt-img"
                        onClick={(ev) => { ev.stopPropagation(); setLightbox({ src: o.image, name: o.text }); }}
                        role="button"
                        tabIndex={0}
                      />
                    )}
                    <span className="poll-opt-text">{o.text}</span>
                    {showInlineResults && (o.pct != null) ? (
                      <span className="poll-opt-pct">{o.pct.toFixed(1)}%</span>
                    ) : (
                      <span className={`poll-radio ${selected.has(o.id) ? "checked" : ""}`}>
                        {selected.has(o.id) && <CheckCircle2 size={16} />}
                      </span>
                    )}
                  </button>
                  {showInlineResults && (o.pct != null) && (
                    <div className="poll-opt-bar"><div className="poll-opt-bar-fill" style={{ width: `${o.pct}%` }} /></div>
                  )}
                </li>
              ))}
            </ul>
            {error && <p className="poll-error">{error}</p>}
            <div className="poll-actions">
              <button
                onClick={submit}
                disabled={busy || selected.size === 0}
                className="poll-submit"
                data-testid={`poll-submit-${poll.slug}`}
              >
                {busy ? "Enviando..." : (poll.allow_multiple ? "Enviar votos" : "Votar")}
              </button>
              {showInlineResults && (
                <Link to={`/encuesta/${poll.slug}/resultados`} className="poll-view-only" data-testid={`poll-only-view-${poll.slug}`}>
                  Ver más
                </Link>
              )}
            </div>
          </>
        )}
      </div>
      {lightbox && (
        <div className="poll-lightbox" onClick={() => setLightbox(null)} data-testid="poll-lightbox-list">
          <button className="poll-lightbox-close" onClick={() => setLightbox(null)} aria-label="Cerrar">✕</button>
          <div className="poll-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            {lightbox.src && <img src={lightbox.src} alt={lightbox.name} className="poll-lightbox-img" />}
            <p className="poll-lightbox-name">{lightbox.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
